// 購買獎勵遊戲彈窗 — 對照 Unity SlotFramework BuyBonus.prefab 的正式兩步流程完整還原：
//   Step1（選擇頁）：頂部押注調整區（pic_popupBg 535×315 @ (0,338)：pic_titleBg 標題「購買獎勵」、
//     「押注」、pic_numBg 押注值 ₱ 金額、btn_minus/btn_plus ±鈕）＋ FREE SPINS 選項卡（cell 430×622：
//     pic_popupBg2 卡底、標題「免費旋轉」、描述（S026 rebind 至 game.Buy_Free「獲得 11 次免費遊戲」）、
//     三張 Scatter 警示牌疊圖（ContentImage @ (0,265)，子圖 (55,-214)/(0,-250)/(-56,-286) scale0.7、
//     sprite 由 CommonPanel.prefab 覆寫為 Slot026 Art/Symbol/SS.png）、pic_numBg 價格、btn_BUY1 購買鈕。
//     本作 PayTable 只有 BuyType2（×100），Super 卡被 Config 關閉且無 BetRatio，故 Step1 僅顯示一張 Free 卡
//     （對應 Unity InitializeBuyOptions 過濾後置中）。
//   Step2（確認頁）：pic_popupBg 680×635、pic_titleBg 標題 @ (0,277)、類型描述（同 game.Buy_Free）@ (0,150)、
//     「免費遊戲需花費」＋ ₱ 金額（無 numBg 底，對照 Unity CostsText 群）@ (0,80)、
//     三張 Scatter 疊圖（Type 群 ContentImage @ (0,50)，子圖 (55,-224)/(0,-260)/(-56,-296)）、
//     btn_start「開始」@ (160,-232)、btn_cancel「取消」@ (-160,-232)。Cancel＝回 Step1（不關窗）。
//   共用：右上關閉鈕（btn_circle 157×157×0.8 ＋ btn_close X 圖）、全螢幕 Mask 點擊關窗。
//   開窗動畫（UIPanelAnimator）：Step1 scale 0→1.2（0.3s InSine）→1（0.1s）、Mask 淡入至 α0.7（0.4s）；關窗即時。
//   餘額不足：開始鈕 disabled 灰化（α0.5，對照 Unity DisabledColor），無提示文字。
//   貨幣符號照 Unity 實機顯示「₱」文字（pic_piso 圖節點 IsActive=0 隱藏）。按鈕/標題文字皆走
//   Unity Common String Table 13 語系（common.Buy_BuyBonus／Buy_Bet／Buy_FreeSpins／Btn_Buy／
//   Buy_Costs／Btn_Start／Btn_Cancel）。
// 佈局：以 Unity 1920×1080 設計座標（中心原點、y 向上→PixiJS 取反）原值排版，整體 min-scale 置中，
//   故所有 anchoredPosition 直接沿用 prefab 數值。確認 → 發 buyBonusRequested（BuyType=2 進免費遊戲）。

import { Container, FillGradient, Graphics, NineSliceSprite, Sprite, Text, type TextStyleOptions } from 'pixi.js'
import type { Orientation, Resizable } from '../../../framework/render/design'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'
import { BUY_BONUS_MULTIPLIER } from '../../constants'
import { formatMoney } from '../../../framework/core/format'
import { getCurrencyColor, getCurrencySymbol } from '../../../framework/core/currency'
import { fontFamilyForLocale, t } from '../../../framework/i18n/i18n'
import { getUiTexture } from '../../assets/UiTextures'
import { killTweensOf, tweenTo } from '../tween'
import { reportModalOpen } from './modalGate'

/** Unity 設計空間（CanvasScaler 1920×1080）。 */
const DESIGN_W = 1920
const DESIGN_H = 1080
/** pic_popupBg 原生 300×300，九宮格邊界 45px 保住邊角。 */
const POPUP_SLICE = 45

/** Unity numBg 金額／₱／花費 label 的淡金奶油色（CommonPanel 覆寫值 #FBF2B4）。
 *  Unity 另有極接近的 #FEF7B2（「押注」label 與免費遊戲描述純色），肉眼不可辨，本移植統一為此值。 */
const CREAM = 0xfbf2b4

/** 建立垂直漸層（上→下）；textureSpace local 讓每個文字用自身高度取色（同 Unity TMP vertex gradient）。 */
function verticalGradient(top: number, bottom: number): FillGradient {
  return new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    colorStops: [
      { offset: 0, color: top },
      { offset: 1, color: bottom },
    ],
    textureSpace: 'local',
  })
}

/** 主面板標題（Unity TMP preset Color_Panel_Title：上 #F3E867 → 下 #F7BE31，黑描邊 α0.5 近似）。 */
function panelTitleStyle(fontSize: number): TextStyleOptions {
  return { fontFamily: fontFamilyForLocale(), fontSize, fill: verticalGradient(0xf3e867, 0xf7be31), fontWeight: 'bold', stroke: { color: 0x000000, width: 4, alpha: 0.5 }, align: 'center' }
}

/** 貨幣強調漸層（Unity preset Color_BottomBar_Currency：上 #FAFAFA → 下 #FFAF00，深紅描邊 #640000）。用於「免費旋轉」卡標題與「購買」鈕。 */
function currencyGradientStyle(fontSize: number): TextStyleOptions {
  return { fontFamily: fontFamilyForLocale(), fontSize, fill: verticalGradient(0xfafafa, 0xffaf00), fontWeight: 'bold', stroke: { color: 0x640000, width: 5 }, align: 'center' }
}

/** 面板按鈕漸層（Unity preset Color_Panel_Button：上 #FDF4B4 → 下 #F7BE31，深綠描邊 #004B00）。用於「開始」「取消」。 */
function panelButtonStyle(fontSize: number): TextStyleOptions {
  return { fontFamily: fontFamilyForLocale(), fontSize, fill: verticalGradient(0xfdf4b4, 0xf7be31), fontWeight: 'bold', stroke: { color: 0x004b00, width: 4 }, align: 'center' }
}

/** 純色淡金內文（無描邊，對照 Unity「押注」label／免費遊戲描述／花費 label）。 */
function creamStyle(fontSize: number): TextStyleOptions {
  return { fontFamily: fontFamilyForLocale(), fontSize, fill: CREAM, fontWeight: 'bold', align: 'center' }
}

/**
 * 三張 Scatter 警示牌疊圖（Unity BuyBonus.prefab ContentImage 的三個子 Image：
 * 子框 214×164 PreserveAspect（SS.png 214×192 → fit 高）＋ scale 0.7，依序疊放（後者在上）。
 * @param offsets 三張圖相對群組錨點的 PixiJS 座標（Unity anchoredPosition 的 y 取反）。
 */
function scatterStack(offsets: ReadonlyArray<readonly [number, number]>): Container {
  const group = new Container()
  const tex = getUiTexture('buyScatter')
  const h = 164 * 0.7
  const w = (tex.width / tex.height) * h
  for (const [x, y] of offsets) {
    const img = new Sprite(tex)
    img.anchor.set(0.5)
    img.setSize(w, h)
    img.position.set(x, y)
    group.addChild(img)
  }
  return group
}

/** 建立置中錨點的九宮格面板（NineSliceSprite 原點在左上，包一層以中心定位）。 */
function centeredPanel(w: number, h: number): NineSliceSprite {
  const ns = new NineSliceSprite({
    texture: getUiTexture('popupBg'),
    leftWidth: POPUP_SLICE,
    topHeight: POPUP_SLICE,
    rightWidth: POPUP_SLICE,
    bottomHeight: POPUP_SLICE,
  })
  ns.width = w
  ns.height = h
  ns.position.set(-w / 2, -h / 2)
  return ns
}

/**
 * 幣別符號＋金額的水平列（兩元件左對齊，refresh 時重新置中）。符號與金額色皆取當前幣別
 * `getCurrencyColor()`——對照 Unity BuyBonus.SetCurrencyUI → `SetCurrencyUI(..., Text, ValueText)`
 * 以預設 `setColor=true` 對「符號＋金額」同套 cur.Color（PHP＝#FAF0B4，與原 CREAM #FBF2B4 近乎同色；
 * COMBO＝粉色）。注意這與 creamStyle() 的「押注／描述／花費」label 不同：那些 Unity 不套幣別色、仍為 CREAM。
 */
class MoneyRow extends Container {
  private readonly currency: Text
  private readonly value: Text
  private readonly gap: number

  constructor(fontSize: number) {
    super()
    this.gap = fontSize * 0.2
    const currencyColor = getCurrencyColor()
    this.currency = new Text({
      text: getCurrencySymbol(),
      style: { fontFamily: fontFamilyForLocale(), fontSize, fill: currencyColor, fontWeight: 'bold' },
    })
    this.value = new Text({
      text: '',
      style: { fontFamily: fontFamilyForLocale(), fontSize, fill: currencyColor, fontWeight: 'bold' },
    })
    this.currency.anchor.set(0, 0.5)
    this.value.anchor.set(0, 0.5)
    this.addChild(this.currency, this.value)
  }

  setValue(amount: number): void {
    this.value.text = formatMoney(amount)
    const total = this.currency.width + this.gap + this.value.width
    this.currency.position.set(-total / 2, 0)
    this.value.position.set(-total / 2 + this.currency.width + this.gap, 0)
  }
}

export class BuyBonusDialog extends Container implements Resizable {
  /** 全螢幕暗背景（Unity Mask，點擊＝關窗；開窗淡入至 0.7）。 */
  private readonly dimBg = new Graphics()
  /** 1920×1080 設計座標層（min-scale 置中）。 */
  private readonly root = new Container()
  private readonly step1 = new Container()
  private readonly step2 = new Container()

  // ── Step1：押注調整區 ──
  private readonly betValue = new MoneyRow(36)
  private readonly minusBtn = new Sprite(getUiTexture('iconMinus'))
  private readonly plusBtn = new Sprite(getUiTexture('iconPlus'))
  // ── Step1：FREE SPINS 選項卡 ──
  private readonly priceValue = new MoneyRow(30)
  private readonly buyBtn = new Container()
  // ── Step2 ──
  private readonly costValue = new MoneyRow(30)
  private readonly startBtn = new Container()
  private readonly cancelBtn = new Container()
  // ── 共用 ──
  private readonly closeBtn = new Container()

  private betOptions: readonly number[] = []
  private betIndex = 0
  private currentBet = 0
  private balance = 0

  constructor(private readonly bus: EventBus<GameEventMap>) {
    super()
    this.visible = false
    this.eventMode = 'static'

    this.dimBg.eventMode = 'static'
    this.dimBg.on('pointertap', () => this.hide())
    this.addChild(this.dimBg, this.root)
    this.root.addChild(this.step1, this.step2, this.closeBtn)

    this.buildStep1()
    this.buildStep2()
    this.buildClose()

    this.bus.on('betConfigChanged', ({ options, index }) => {
      this.betOptions = options
      this.betIndex = index
      this.refreshIfOpen()
    })
    this.bus.on('betChanged', ({ bet }) => {
      this.currentBet = bet
      this.refreshIfOpen()
    })
    this.bus.on('balanceChanged', ({ balance }) => {
      this.balance = balance
      this.refreshIfOpen()
    })
    this.bus.on('buyBonusClicked', () => this.show())
    // 進入旋轉/免費遊戲時強制關閉（避免購買後彈窗殘留）。
    this.bus.on('spinStateChanged', ({ spinning }) => {
      if (spinning) this.hide()
    })
  }

  private get cost(): number {
    return this.currentBet * BUY_BONUS_MULTIPLIER
  }

  private get affordable(): boolean {
    return this.balance >= this.cost
  }

  get isOpen(): boolean {
    return this.visible
  }

  // ── 元件建立（Unity anchoredPosition 原值，y 取反） ──

  /** Step1：頂部押注調整區 @ (0,338) ＋ 選項卡（Grid cell 430×622，content top y=161.22、padding 35）。 */
  private buildStep1(): void {
    // 押注調整區（Bet 群組）
    const betGroup = new Container()
    betGroup.position.set(0, -338)
    const betBg = centeredPanel(535, 315)
    const betTitleBg = new Sprite(getUiTexture('titleBg'))
    betTitleBg.anchor.set(0.5)
    betTitleBg.setSize(500, 78)
    betTitleBg.position.set(0, -117)
    const betTitle = new Text({ text: t('common.Buy_BuyBonus'), style: panelTitleStyle(36) })
    betTitle.anchor.set(0.5)
    betTitle.position.set(0, -117)
    const betLabel = new Text({ text: t('common.Buy_Bet'), style: creamStyle(30) })
    betLabel.anchor.set(0.5)
    betLabel.position.set(0, -19)
    const betNumBg = new Sprite(getUiTexture('numBg'))
    betNumBg.anchor.set(0.5)
    // Unity NumBg 330×54 scale(1.2,1.25)
    betNumBg.setSize(396, 67.5)
    betNumBg.position.set(0, 63)
    this.betValue.position.set(0, 63)
    this.minusBtn.anchor.set(0.5)
    this.minusBtn.setSize(100, 100)
    this.minusBtn.position.set(-190, 63)
    this.plusBtn.anchor.set(0.5)
    this.plusBtn.setSize(100, 100)
    this.plusBtn.position.set(190, 63)
    for (const btn of [this.minusBtn, this.plusBtn]) {
      btn.eventMode = 'static'
      btn.cursor = 'pointer'
    }
    this.minusBtn.on('pointertap', () => this.stepBet(-1))
    this.plusBtn.on('pointertap', () => this.stepBet(1))
    betGroup.addChild(betBg, betTitleBg, betTitle, betLabel, betNumBg, this.betValue, this.minusBtn, this.plusBtn)

    // FREE SPINS 選項卡（唯一一張，Grid 置中；卡中心 y = 161.22 − 35 − 622/2 ≈ −184.8）
    const card = new Container()
    card.position.set(0, 184.8)
    const cardBg = new Sprite(getUiTexture('popupBg2'))
    cardBg.anchor.set(0.5)
    cardBg.setSize(430, 500)
    cardBg.position.set(0, -60)
    const cardTitle = new Text({ text: t('common.Buy_FreeSpins'), style: currencyGradientStyle(26) })
    cardTitle.anchor.set(0.5)
    cardTitle.position.set(0, -265)
    const cardDesc = new Text({
      text: t('game.Buy_Free'),
      style: { ...creamStyle(27), wordWrap: true, wordWrapWidth: 400 },
    })
    cardDesc.anchor.set(0.5, 0)
    cardDesc.position.set(0, -235)
    // 三張 Scatter 疊圖（ContentImage @ (0,265)＋子圖 (55,-214)/(0,-250)/(-56,-286)，換算為卡內絕對座標）。
    const cardScatters = scatterStack([
      [55, -51],
      [0, -15],
      [-56, 21],
    ])
    const priceNumBg = new Sprite(getUiTexture('numBg'))
    priceNumBg.anchor.set(0.5)
    priceNumBg.setSize(330, 54)
    priceNumBg.position.set(0, 125)
    this.priceValue.position.set(0, 125)
    const buyBg = new Sprite(getUiTexture('btnBuy1'))
    buyBg.anchor.set(0.5)
    buyBg.setSize(440, 119)
    const buyLabel = new Text({ text: t('common.Btn_Buy'), style: currencyGradientStyle(42) })
    buyLabel.anchor.set(0.5)
    this.buyBtn.addChild(buyBg, buyLabel)
    this.buyBtn.position.set(0, 250)
    this.buyBtn.eventMode = 'static'
    this.buyBtn.cursor = 'pointer'
    this.buyBtn.on('pointertap', () => this.openStep(2))
    card.addChild(cardBg, cardTitle, cardDesc, cardScatters, priceNumBg, this.priceValue, this.buyBtn)

    this.step1.addChild(betGroup, card)
  }

  /** Step2：確認頁（pic_popupBg 680×635）。 */
  private buildStep2(): void {
    const bg = centeredPanel(680, 635)
    const titleBg = new Sprite(getUiTexture('titleBg'))
    titleBg.anchor.set(0.5)
    titleBg.setSize(635, 78)
    titleBg.position.set(0, -277)
    const title = new Text({ text: t('common.Buy_BuyBonus'), style: panelTitleStyle(36) })
    title.anchor.set(0.5)
    title.position.set(0, -277)
    // 類型描述（Unity Type/Free 子樹 @ y=150，S026 rebind 至 game.Buy_Free）
    const typeDesc = new Text({
      text: t('game.Buy_Free'),
      style: { ...creamStyle(29), wordWrap: true, wordWrapWidth: 560 },
    })
    typeDesc.anchor.set(0.5)
    typeDesc.position.set(0, -150)
    // 三張 Scatter 疊圖（Type 群 @ y=150 ＋ ContentImage (0,50) → 錨點 y=200；子圖 (55,-224)/(0,-260)/(-56,-296)）。
    const typeScatters = scatterStack([
      [55, 24],
      [0, 60],
      [-56, 96],
    ])
    // 花費（CostsText 垂直群 @ y=80：標籤在上、₱ 金額在下，無 numBg 底）
    const costsLabel = new Text({ text: t('common.Buy_Costs'), style: creamStyle(30) })
    costsLabel.anchor.set(0.5)
    costsLabel.position.set(0, -105)
    this.costValue.position.set(0, -55)
    // 開始／取消鈕（256×92 @ (±160,-232)）
    const startBg = new Sprite(getUiTexture('btnStart'))
    startBg.anchor.set(0.5)
    startBg.setSize(256, 92)
    const startLabel = new Text({ text: t('common.Btn_Start'), style: panelButtonStyle(34) })
    startLabel.anchor.set(0.5)
    this.startBtn.addChild(startBg, startLabel)
    this.startBtn.position.set(160, 232)
    const cancelBg = new Sprite(getUiTexture('btnCancel'))
    cancelBg.anchor.set(0.5)
    cancelBg.setSize(256, 92)
    const cancelLabel = new Text({ text: t('common.Btn_Cancel'), style: panelButtonStyle(34) })
    cancelLabel.anchor.set(0.5)
    this.cancelBtn.addChild(cancelBg, cancelLabel)
    this.cancelBtn.position.set(-160, 232)
    for (const btn of [this.startBtn, this.cancelBtn]) {
      btn.eventMode = 'static'
      btn.cursor = 'pointer'
    }
    this.startBtn.on('pointertap', () => this.onConfirm())
    // Cancel＝回 Step1（對照 Unity OnBuyFreeSpinsCancel，不關窗）。
    this.cancelBtn.on('pointertap', () => this.openStep(1))

    this.step2.addChild(bg, titleBg, title, typeDesc, typeScatters, costsLabel, this.costValue, this.startBtn, this.cancelBtn)
  }

  /** 右上關閉鈕（Unity Close：btn_circle 157×157 scale0.8＋btn_close 84×84 scale1.2，角落內縮 10px）。 */
  private buildClose(): void {
    const circle = new Sprite(getUiTexture('btnCircle'))
    circle.anchor.set(0.5)
    circle.setSize(157 * 0.8, 157 * 0.8)
    const cross = new Sprite(getUiTexture('btnClose'))
    cross.anchor.set(0.5)
    cross.setSize(84 * 1.2 * 0.8, 84 * 1.2 * 0.8)
    this.closeBtn.addChild(circle, cross)
    const half = (157 * 0.8) / 2
    this.closeBtn.position.set(DESIGN_W / 2 - 10 - half, -(DESIGN_H / 2 - 10 - half))
    this.closeBtn.eventMode = 'static'
    this.closeBtn.cursor = 'pointer'
    this.closeBtn.on('pointertap', () => this.hide())
  }

  // ── 開關與流程 ──

  /** 開窗（對應 OnBuyBonusClick）：刷新 → 顯示 Step1 → PopUp 動畫。 */
  show(): void {
    this.refresh()
    this.openStep(1)
    this.visible = true
    reportModalOpen(this, true)
    this.playPopUp()
  }

  /** 關窗（對應 UIPanelAnimator.Close：即時，無淡出）。 */
  hide(): void {
    killTweensOf(this.dimBg)
    killTweensOf(this.step1.scale)
    this.visible = false
    reportModalOpen(this, false)
  }

  /** 切換 Step1/Step2（對照 Unity OpenStepPanel 以 CanvasGroup 切換）。 */
  private openStep(step: 1 | 2): void {
    this.step1.visible = step === 1
    this.step2.visible = step === 2
    if (step === 2) this.refresh()
  }

  /** 開窗動畫（UIPanelAnimator.PopUp）：Step1 scale 0→1.2（0.3s InSine）→1（0.1s）、Mask 淡入 0.7（0.4s）。 */
  private playPopUp(): void {
    killTweensOf(this.dimBg)
    killTweensOf(this.step1.scale)
    this.dimBg.alpha = 0
    void tweenTo(this.dimBg, { alpha: 1, duration: 0.4 })
    this.step1.scale.set(0)
    this.eventMode = 'none'
    void tweenTo(this.step1.scale, { x: 1.2, y: 1.2, duration: 0.3, ease: 'sine.in' }).then(async () => {
      await tweenTo(this.step1.scale, { x: 1, y: 1, duration: 0.1 })
      this.eventMode = 'static'
    })
  }

  /** 押注 ±（對照 Unity 以 BetRange 索引調整；到邊界時鈕 disabled）。 */
  private stepBet(delta: number): void {
    const next = this.betIndex + delta
    if (next < 0 || next >= this.betOptions.length) return
    this.betIndex = next
    this.bus.emit('betSelected', { index: next })
  }

  private onConfirm(): void {
    if (!this.affordable) return
    this.hide()
    this.bus.emit('buyBonusRequested', undefined)
  }

  private refreshIfOpen(): void {
    if (this.isOpen) this.refresh()
  }

  /** 刷新押注/價格/花費與 ± 邊界、開始鈕可用狀態。 */
  private refresh(): void {
    this.betValue.setValue(this.currentBet)
    this.priceValue.setValue(this.cost)
    this.costValue.setValue(this.cost)
    const atMin = this.betIndex <= 0
    const atMax = this.betIndex >= this.betOptions.length - 1
    this.setEnabled(this.minusBtn, !atMin)
    this.setEnabled(this.plusBtn, !atMax)
    // 餘額不足：開始鈕 disabled 灰化（對照 Unity Button DisabledColor a=0.5），無提示文字。
    this.setEnabled(this.startBtn, this.affordable)
  }

  private setEnabled(target: Container, enabled: boolean): void {
    target.alpha = enabled ? 1 : 0.5
    target.eventMode = enabled ? 'static' : 'none'
    target.cursor = enabled ? 'pointer' : 'default'
  }

  resize(width: number, height: number, _orientation: Orientation): void {
    this.dimBg.clear().rect(0, 0, width, height).fill({ color: 0x000000, alpha: 0.7 })
    const scale = Math.min(width / DESIGN_W, height / DESIGN_H)
    this.root.scale.set(scale)
    this.root.position.set(width / 2, height / 2)
  }
}
